"use client";

import React from "react";
import {
  FileCheck,
  Stethoscope,
  Building2,
  Lock,
  Award,
  Zap,
  ShieldCheck,
} from "lucide-react";
import { DIAGNOSTIC_LAB_PARTNERS, UNDERWRITING_DOCTORS } from "@/lib/demos/insuranceData";

export default function UnderwritingTrustSection() {
  return (
    <section className="py-20 bg-[#060c1c] border-t border-blue-950/60">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        
        {/* Underwriting Process Architecture */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950/70 border border-emerald-800/60 text-emerald-300 text-xs font-semibold mb-3">
            <Zap className="w-3 h-3 text-emerald-400" />
            Digital-First Actuarial Architecture
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
            Seamless Underwriting, Zero Paperwork
          </h2>
          <p className="text-slate-400 text-sm sm:text-base mt-3">
            Transforming traditional multi-week insurance delays into a 24-hour transparent process with algorithmic mortality risk models and 100% sponsored executive medical assessments.
          </p>
        </div>

        {/* 4 Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-20">
          <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 relative">
            <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400 font-bold text-xs mb-4">
              01
            </div>
            <h4 className="text-sm font-bold text-white">Instant Aadhaar CKYC</h4>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              Paperless identity and address verification directly synced with the Central KYC Registry and UIDAI databases.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 relative">
            <div className="w-8 h-8 rounded-lg bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400 font-bold text-xs mb-4">
              02
            </div>
            <h4 className="text-sm font-bold text-white">Sponsored Diagnostic Pass</h4>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              Automated PDF medical examination pass for comprehensive lipid, ECG &amp; renal panels at premier diagnostic centers.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 relative">
            <div className="w-8 h-8 rounded-lg bg-indigo-600/20 border border-indigo-500/40 flex items-center justify-center text-indigo-400 font-bold text-xs mb-4">
              03
            </div>
            <h4 className="text-sm font-bold text-white">Actuarial Risk Assessment</h4>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              Senior medical director review assessing medical records and underwriting financial risk without hidden exclusions.
            </p>
          </div>

          <div className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 relative">
            <div className="w-8 h-8 rounded-lg bg-amber-600/20 border border-amber-500/40 flex items-center justify-center text-amber-400 font-bold text-xs mb-4">
              04
            </div>
            <h4 className="text-sm font-bold text-white">Digital Policy Schedule</h4>
            <p className="text-xs text-slate-400 mt-2 leading-relaxed">
              Instant policy contract issuance with official IRDAI registration and legally endorsed Section 80C/80D tax certificates.
            </p>
          </div>
        </div>

        {/* Two Columns: Medical Underwriting Directorate & Diagnostic Lab Partners */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Medical Directorate */}
          <div className="lg:col-span-6 p-6 sm:p-8 rounded-2xl bg-slate-900/80 border border-slate-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Medical Underwriting Directorate</h3>
                <p className="text-xs text-slate-400">Actuarial &amp; clinical risk evaluation officers</p>
              </div>
            </div>

            <div className="space-y-4">
              {UNDERWRITING_DOCTORS.map((doc, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-950/80 border border-slate-800/80">
                  <div className="font-bold text-sm text-white">{doc.name}</div>
                  <div className="text-xs text-blue-400 font-medium mt-0.5">{doc.title}</div>
                  <div className="text-[11px] text-slate-400 mt-1">{doc.degrees}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Diagnostic Laboratory Partners */}
          <div className="lg:col-span-6 p-6 sm:p-8 rounded-2xl bg-slate-900/80 border border-slate-800">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-emerald-600/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Accredited Diagnostic Network</h3>
                <p className="text-xs text-slate-400">Sponsored medical examination &amp; Tele-MER centers</p>
              </div>
            </div>

            <div className="space-y-4">
              {DIAGNOSTIC_LAB_PARTNERS.map((lab, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-slate-950/80 border border-slate-800/80">
                  <div className="font-bold text-sm text-white">{lab.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{lab.address}</div>
                  <div className="text-[11px] text-emerald-400 font-medium mt-1">
                    Specialty: {lab.facility}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
